import { Box, Typography, useTheme, TypographyProps } from '@mui/material';
import * as React from 'react';
import { FaOptionIcon, orderAscIcon, orderDescIcon, orderNoIcon } from '@europrocurement/l2d-icons';
import { OrdersOpts } from '../DatatableHeader';
import { ColumnDatatable } from '../ColumnDatatable';

export type SortDirection = 'asc' | 'desc' | undefined;

type ColumnHeaderItemSortabledProps = {
    onOrdersChange: OrdersOpts['onOrdersChange'];
    field: ColumnDatatable['field'];
    defaultValue: SortDirection;
    label: string;
    labelVariant?: TypographyProps['variant'];
    defaultLabelColor?: TypographyProps['color'];
};

export const ColumnHeaderItemSortabled: React.FunctionComponent<ColumnHeaderItemSortabledProps> =
    function ({
        onOrdersChange,
        field,
        defaultValue,
        label,
        labelVariant,
        defaultLabelColor = 'text.gris50',
    }: ColumnHeaderItemSortabledProps) {
        const theme = useTheme();

        // Determine if the column is actively sorted
        const isActiveSort = defaultValue !== undefined;

        // Get synchronized color for both label and icon
        const getActiveColor = () => {
            if (!isActiveSort) return defaultLabelColor;
            return theme.palette.secondary.main;
        };

        // Handle sort direction cycling
        const handleClick = () => {
            let newDirection: SortDirection;

            if (!defaultValue) {
                newDirection = 'asc';
            } else if (defaultValue === 'asc') {
                newDirection = 'desc';
            } else {
                newDirection = undefined;
            }

            onOrdersChange(`${field}`, newDirection);
        };

        // Render the appropriate icon based on sort state
        const renderSortIcon = () => {
            const iconColor = getActiveColor();

            switch (defaultValue) {
                case 'asc':
                    return (
                        <FaOptionIcon
                            {...orderAscIcon.props}
                            size="xs"
                            color={iconColor}
                        />
                    );
                case 'desc':
                    return (
                        <FaOptionIcon
                            {...orderDescIcon.props}
                            size="xs"
                            color={iconColor}
                        />
                    );
                default:
                    return (
                        <FaOptionIcon
                            {...orderNoIcon.props}
                            size="xs"
                            color={iconColor}
                            iconProps={{
                                opacity: '40%',
                            }}
                        />
                    );
            }
        };

        return (
            <Box
                onClick={handleClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: 1,
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                        backgroundColor: 'action.hover',
                    },
                }}
            >
                <Typography
                    variant={labelVariant}
                    color={getActiveColor()}
                    sx={{
                        fontWeight: isActiveSort ? 700 : 600,
                        transition: 'color 0.2s ease',
                    }}
                >
                    {label}
                </Typography>
                {renderSortIcon()}
            </Box>
        );
    };
